// src/resources/units/components/RevokeKeysBulkAction.tsx
import { FC, useState } from 'react';
import { Button, useListContext, useRefresh, useNotify, useUnselectAll, usePermissions } from 'react-admin';
import { Key as KeyIcon } from '@mui/icons-material';
import BulkRevokeKeysModal from './BulkRevokeKeysModal';

const RevokeKeysBulkAction: FC = () => {
  const { selectedIds, data } = useListContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll('units');
  const { permissions } = usePermissions();

  // Check if user has permission
  const hasPermission = permissions?.includes('manage_device_keys');

  // Filter selected units - only show button if at least one has a valid key
  // data is an array: [{id: 1, ssh_key_state: 'valid'}, {id: 2, ssh_key_state: 'revoked'}, ...]
  const selectedUnits = (data as any[])?.filter((unit) => selectedIds.includes(unit?.id)) || [];
  const unitsWithValidKeys = selectedUnits.filter((unit) => unit?.ssh_key_state === 'valid');

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSuccess = (successCount: number, failureCount: number) => {
    if (failureCount === 0) {
      notify(`Successfully revoked ${successCount} SSH key(s)`, { type: 'success' });
    } else {
      notify(`Revoked ${successCount} key(s), ${failureCount} failed`, { type: 'warning' });
    }
    unselectAll();
    refresh();
    setIsModalOpen(false);
  };

  const handleError = (error: any) => {
    notify(error?.message || 'Failed to revoke SSH keys', { type: 'error' });
  };

  // Don't show button if no permission or no valid keys selected
  if (!hasPermission || unitsWithValidKeys.length === 0) {
    return null;
  }

  return (
    <>
      <Button
        label={`Revoke ${unitsWithValidKeys.length} Key(s)`}
        onClick={handleModalOpen}
        startIcon={<KeyIcon />}
        variant="text"
      />
      <BulkRevokeKeysModal
        open={isModalOpen}
        onClose={handleModalClose}
        units={unitsWithValidKeys}
        onSuccess={handleSuccess}
        onError={handleError}
      />
    </>
  );
};

export default RevokeKeysBulkAction;
